import React, { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import {
  Search, ChevronLeft, ChevronRight, Target,
  UserPlus, Phone, Edit, Eye, Trash2, FileSpreadsheet
} from 'lucide-react';
import { cn } from '@/lib/utils';

const StudentsContent = ({
  students,
  studentFilter,
  setStudentFilter,
  counselors,
  selectedStudents,
  setSelectedStudents,
  setActiveSection,
  setShowExcelModal,
  setShowAddStudentModal,
  setSelectedStudent,
  setShowStudentModal,
  openEditStudent,
  openStudentModal,
  handleSendSMS,
  handleSendBulkSMS,
  handleDeleteStudent,
  handleDeleteSelectedStudents,
  toggleStudentSelection,
  getCounselorName,
  smsLoading,
  smsSendingId,
  deleteLoading
}) => {
  const [localSearch, setLocalSearch] = useState('');
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [dateFilter, setDateFilter] = useState({ start: '', end: '' });
  const [counselorFilter, setCounselorFilter] = useState('');
  
  // Autocomplete sonuçları - sadece arama yapılınca
  const autocompleteResults = localSearch.length >= 2 
    ? students.filter(s => 
        s.name?.toLowerCase().includes(localSearch.toLowerCase()) ||
        s.email?.toLowerCase().includes(localSearch.toLowerCase()) ||
        s.phone?.includes(localSearch)
      ).slice(0, 8)
    : [];
  
  // Filtrelenmiş tam liste
  const getFilteredList = () => {
    let result = students;
    
    // Metin filtresi
    if (localSearch) {
      result = result.filter(s => 
        s.name?.toLowerCase().includes(localSearch.toLowerCase()) ||
        s.email?.toLowerCase().includes(localSearch.toLowerCase()) ||
        s.phone?.includes(localSearch)
      );
    }
    
    // Durum filtresi
    if (studentFilter === 'never_logged_in') {
      result = result.filter(s => !s.last_login);
    } else if (studentFilter === 'no_agreements') {
      result = result.filter(s => !s.agreements_accepted);
    } else if (studentFilter === 'unpaid') {
      result = result.filter(s => s.payment_status !== 'paid');
    } else if (studentFilter === 'frozen') {
      result = result.filter(s => s.is_frozen);
    }
    
    // Danışman filtresi
    if (counselorFilter) {
      result = result.filter(s => s.counselor_id === counselorFilter);
    }
    
    // Tarih filtresi (kayıt tarihi)
    if (dateFilter.start) {
      result = result.filter(s => new Date(s.created_at) >= new Date(dateFilter.start));
    }
    if (dateFilter.end) {
      result = result.filter(s => new Date(s.created_at) <= new Date(dateFilter.end));
    }
    
    return result;
  };
  
  const filteredList = getFilteredList();
  
  return (
    <div className="space-y-6">
      {/* Geri Butonu */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => setActiveSection('student-hub')}>
          <ChevronLeft className="w-4 h-4 mr-2" />
          Geri
        </Button>
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Öğrenci Listesi</h2>
          <p className="text-gray-500">Toplam {students.length} kayıtlı öğrenci</p>
        </div>
      </div>

      {/* Arama ve Aksiyon Butonları */}
      <Card className="border-0 shadow-lg">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Autocomplete Arama */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Öğrenci adı, e-posta veya telefon yazın..."
                value={localSearch}
                onChange={(e) => {
                  setLocalSearch(e.target.value);
                  setShowAutocomplete(true);
                }}
                onFocus={() => setShowAutocomplete(true)}
                onBlur={() => setTimeout(() => setShowAutocomplete(false), 200)}
                className="pl-10 h-12 text-lg"
                data-testid="student-search-input"
              />
              
              {/* Autocomplete Dropdown */}
              {showAutocomplete && autocompleteResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto">
                  {autocompleteResults.map(student => (
                    <div 
                      key={student.id}
                      className="flex items-center justify-between p-3 hover:bg-blue-50 cursor-pointer border-b last:border-0"
                      onClick={() => {
                        setSelectedStudent(student);
                        setShowStudentModal(true);
                        setShowAutocomplete(false);
                      }}
                      data-testid={`student-autocomplete-${student.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-semibold">
                          {student.name?.charAt(0)}
                        </div>
                        <div>
                          <p className="font-medium">{student.name}</p>
                          <p className="text-sm text-gray-500">{student.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {student.agreements_accepted ? (
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">✓</span>
                        ) : (
                          <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded">!</span>
                        )}
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Aksiyon Butonları */}
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="border-green-500 text-green-600 hover:bg-green-50" 
                onClick={() => setShowExcelModal(true)}
                data-testid="excel-import-btn"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Excelden Aktar
              </Button>
              <Button 
                className="bg-blue-600 hover:bg-blue-700" 
                onClick={() => setShowAddStudentModal(true)}
                data-testid="add-student-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Yeni Öğrenci
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Gelişmiş Filtreler */}
      <Card className="border-0 shadow-lg">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-5 h-5 text-gray-500" />
            <span className="font-semibold text-gray-700">Filtreler</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Durum Filtresi */}
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Durum</label>
              <select
                value={studentFilter}
                onChange={(e) => setStudentFilter(e.target.value)}
                className="w-full h-10 px-3 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="status-filter"
              >
                <option value="">Tüm Öğrenciler</option>
                <option value="never_logged_in">Hiç Giriş Yapmamış</option>
                <option value="no_agreements">Sözleşme Kabul Etmemiş</option>
                <option value="unpaid">Ödeme Yapmamış</option>
                <option value="frozen">❄️ Kaydı Dondurulmuş</option>
              </select>
            </div>
            
            {/* Danışman Filtresi */}
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Danışman</label>
              <select
                value={counselorFilter}
                onChange={(e) => setCounselorFilter(e.target.value)}
                className="w-full h-10 px-3 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="counselor-filter"
              >
                <option value="">Tüm Danışmanlar</option>
                {counselors.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            
            {/* Tarih Filtresi - Başlangıç */}
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Kayıt Tarihi (Başlangıç)</label>
              <Input
                type="date"
                value={dateFilter.start}
                onChange={(e) => setDateFilter(prev => ({ ...prev, start: e.target.value }))}
                className="h-10"
              />
            </div>
            
            {/* Tarih Filtresi - Bitiş */}
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Kayıt Tarihi (Bitiş)</label>
              <Input
                type="date"
                value={dateFilter.end}
                onChange={(e) => setDateFilter(prev => ({ ...prev, end: e.target.value }))}
                className="h-10"
              />
            </div>
          </div>
          
          {/* Filtre Özeti ve Temizle */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t">
            <span className="text-sm text-gray-500">
              {filteredList.length} / {students.length} öğrenci gösteriliyor
            </span>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  setStudentFilter('');
                  setCounselorFilter('');
                  setDateFilter({ start: '', end: '' });
                  setLocalSearch('');
                }}
                data-testid="clear-filters-btn"
              >
                Filtreleri Temizle
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Seçili Öğrenci Aksiyonları */}
      {selectedStudents.length > 0 && (
        <div className="flex items-center justify-between p-4 bg-red-50 rounded-xl border border-red-200">
          <span className="text-red-700 font-medium">
            {selectedStudents.length} öğrenci seçildi
          </span>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              className="border-gray-300 text-gray-600"
              onClick={() => setSelectedStudents([])}
            >
              Seçimi Temizle
            </Button>
            <Button 
              className="bg-green-500 hover:bg-green-600 text-white"
              onClick={handleSendBulkSMS}
              disabled={smsLoading}
            >
              <Phone className="w-4 h-4 mr-2" />
              {smsLoading ? 'Gönderiliyor...' : `${selectedStudents.length} Kişiye SMS`}
            </Button>
            <Button 
              className="bg-red-500 hover:bg-red-600 text-white"
              onClick={handleDeleteSelectedStudents}
              disabled={deleteLoading}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {deleteLoading ? 'Siliniyor...' : `${selectedStudents.length} Öğrenciyi Sil`}
            </Button>
          </div>
        </div>
      )}

      {/* Öğrenci Tablosu */}
      <Card className="border-0 shadow-lg">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="students-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="p-4 w-12">
                    <input
                      type="checkbox"
                      checked={selectedStudents.length === filteredList.length && filteredList.length > 0}
                      onChange={() => {
                        if (selectedStudents.length === filteredList.length) {
                          setSelectedStudents([]);
                        } else {
                          setSelectedStudents(filteredList.map(s => s.id));
                        }
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="text-left p-4 text-sm font-semibold text-gray-600">Öğrenci</th>
                  <th className="text-left p-4 text-sm font-semibold text-gray-600">Danışman</th>
                  <th className="text-left p-4 text-sm font-semibold text-gray-600">Durum</th>
                  <th className="text-left p-4 text-sm font-semibold text-gray-600">Son Giriş</th>
                  <th className="text-left p-4 text-sm font-semibold text-gray-600">İlerleme</th>
                  <th className="text-left p-4 text-sm font-semibold text-gray-600">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {filteredList.map((student) => (
                  <tr 
                    key={student.id} 
                    className={cn(
                      "border-b hover:bg-gray-50 transition-colors",
                      selectedStudents.includes(student.id) && "bg-red-50 hover:bg-red-100",
                      student.is_frozen && "bg-blue-50/50"
                    )}
                    data-testid={`student-row-${student.id}`}
                  >
                    <td className="p-4">
                      <input
                        type="checkbox"
                        checked={selectedStudents.includes(student.id)}
                        onChange={() => toggleStudentSelection(student.id)}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-10 h-10 rounded-full flex items-center justify-center font-medium relative",
                          student.role === 'admin' ? 'bg-purple-100 text-purple-600' : 
                          student.is_frozen ? 'bg-blue-200 text-blue-600' : 'bg-blue-100 text-blue-600'
                        )}>
                          {student.is_frozen ? '❄️' : (student.role === 'admin' ? '👑' : student.name?.charAt(0))}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-gray-800">{student.name}</p>
                            {student.is_frozen && (
                              <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-medium">DONDURULMUŞ</span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">{student.email}</p>
                          {student.phone && <p className="text-xs text-gray-400">{student.phone}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={cn(
                        "text-sm",
                        student.counselor_id ? "text-purple-600 font-medium" : "text-gray-400"
                      )}>
                        {getCounselorName(student.counselor_id)}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex flex-col gap-1">
                        {student.is_frozen ? (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">❄️ Kayıt Donduruldu</span>
                        ) : student.last_login ? (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">✓ Giriş Yaptı</span>
                        ) : (
                          <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs">⚠ Hiç Girmedi</span>
                        )}
                        {student.agreements_accepted ? (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">📄 Sözleşme ✓</span>
                        ) : (
                          <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">📄 Sözleşme ✗</span>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-sm text-gray-600">
                        {student.last_login 
                          ? new Date(student.last_login).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })
                          : '-'}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-[100px]">
                          <div 
                            className="bg-blue-500 h-2 rounded-full" 
                            style={{ width: `${Math.min((student.miles || 0) / 50, 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500">{student.miles || 0} mil</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditStudent(student)} title="Düzenle">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openStudentModal(student)} title="Detay">
                          <Eye className="w-4 h-4" />
                        </Button>
                        {student.phone && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleSendSMS(student)}
                            className="text-green-500 hover:text-green-700 hover:bg-green-50"
                            title="SMS Gönder"
                            disabled={smsLoading && smsSendingId === student.id}
                          >
                            {smsLoading && smsSendingId === student.id ? (
                              <div className="w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Phone className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleDeleteStudent(student.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          title="Sil"
                          disabled={deleteLoading}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StudentsContent;
